import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, AlertTriangle, Clock, Crown, Zap, Check, Lock, ShieldCheck } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useSimulatorI18n } from "../i18n/simulator";
import { SCENARIOS, generateCandle } from "../data/simulatorScenarios";
import { CandleChart } from "../components/simulator/CandleChart";
import { AIMentorPanel } from "../components/simulator/AIMentorPanel";
import { OrderPanel } from "../components/simulator/OrderPanel";
import { PositionPanel } from "../components/simulator/PositionPanel";
import { MetricsFooter } from "../components/simulator/MetricsFooter";
import { trackSimulatorStart } from "../lib/analytics";

import { API_BASE } from "../lib/api"; 
import { safeJson } from "../lib/api";
const TICK_NORMAL_MS = 1500;
const TICK_FAST_MS = 500;

export default function SimulatorPlayPage() {
  const { lang } = useLanguage();
  const tSim = useSimulatorI18n(lang);
  const { user, isPro } = useAuth();
  const navigate = useNavigate();

  // Scenario selection — FREE locked to FTX
  const scenarioId = "ftx_collapse";
  const scenario = SCENARIOS[scenarioId];

  // ---- state ----
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [runId, setRunId] = useState(null);
  const [candles, setCandles] = useState(() => [
    { open: scenario.startPrice, high: scenario.startPrice, low: scenario.startPrice, close: scenario.startPrice, volume: 500 },
  ]);
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [finished, setFinished] = useState(false);
  const [capital, setCapital] = useState(scenario.initialCapital);
  const [position, setPosition] = useState(null);
  const [trades, setTrades] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [mentorMessages, setMentorMessages] = useState([]);
  const [mentorLoading, setMentorLoading] = useState(false);
  const [metrics, setMetrics] = useState(scenario.base);
  const [eventBanner, setEventBanner] = useState(null);
  const [idleCandles, setIdleCandles] = useState(0);

  // Refs for trigger throttling
  const lastMentorCandleRef = useRef(-10);
  const triggeredRef = useRef(new Set()); // scenario events fired
  const lastDecisionsRef = useRef([]);

  // FREE mentor message cap = 3
  const mentorCap = isPro ? 999 : 3;

  const currentCandle = candles[candles.length - 1];
  const currentPrice = currentCandle.close;
  const params = useMemo(() => scenario.params(step), [scenario, step]);
  const highVolatility = params.vol > 0.03;

  // ---- access check + create run (idempotent against StrictMode double-mount) ----
  const startCalledRef = useRef(false);
  useEffect(() => {
    const start = async () => {
      if (user === null) return; // auth still loading
      if (user === false) {
        setAccessDenied("login");
        setAccessChecked(true);
        return;
      }
      if (startCalledRef.current) return;
      startCalledRef.current = true;

      // NOTE: we used to cache run_id in sessionStorage by scenario, but that allowed
      // a free user to refresh the page or re-enter the same scenario to bypass the
      // single-use trial. The backend has the only authoritative quota: every fresh
      // page load must hit /simulator/start so the backend can either grant a new
      // run or return 402 paywall.
      try {
        const token = localStorage.getItem("rebel_jwt");
        const API = API_BASE; // runtime-resolved, see /lib/api.js
        const res = await fetch(`${API}/api/simulator/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ scenario_id: scenarioId, mode: "historical_survival" }),
        });
        // IMPORTANT: check status BEFORE parsing the body, otherwise a malformed
        // JSON response (or a 3rd-party fetch interceptor that already consumed
        // the body) would throw and we'd mis-route a 402 → "error" instead of
        // "paywall", hiding the upsell.
        if (res.status === 402) {
          setAccessDenied("paywall");
          setAccessChecked(true);
          return;
        }
        if (!res.ok) {
          setAccessDenied("error");
          setAccessChecked(true);
          return;
        }
        const data = await safeJson(res);
        if (!data?.run_id) {
          setAccessDenied("error");
          setAccessChecked(true);
          return;
        }
        setRunId(data.run_id);
        setAccessChecked(true);
        try { trackSimulatorStart({ scenarioId, isPro }); } catch {}
      } catch (e) {
        setAccessDenied("error");
        setAccessChecked(true);
      }
    };
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ---- mentor API call ----
  const callMentor = useCallback(
    async (trigger, label) => {
      if (mentorMessages.length >= mentorCap) return;
      if (mentorLoading) return;
      setMentorLoading(true);
      try {
        const API = API_BASE;  // runtime-resolved, see /lib/api.js
        const token = localStorage.getItem("rebel_jwt");
        const res = await fetch(`${API}/api/simulator/mentor`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            trigger,
            lang,
            context: {
              scenario: scenario.name[lang] || scenario.name.es,
              price: currentPrice,
              volatility: params.vol,
              user_action: lastDecisionsRef.current[lastDecisionsRef.current.length - 1] || null,
              position: position ? position.side : "flat",
              pnl: position ? ((currentPrice - position.entry) / position.entry) * 100 * (position.side === "long" ? 1 : -1) : 0,
              decision_history: lastDecisionsRef.current.slice(-5),
            },
          }),
        });
        const data = await safeJson(res);
        if (res.ok && data.message) {
          setMentorMessages((prev) => [...prev, { id: `${trigger}-${Date.now()}`, text: data.message, label }]);
          lastMentorCandleRef.current = step;
        }
      } catch (e) {
        // silent
      } finally {
        setMentorLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mentorMessages.length, mentorLoading, lang, currentPrice, params.vol, position, step]
  );

  // ---- scenario_start mentor fire (once when ready) ----
  useEffect(() => {
    if (accessChecked && !accessDenied && mentorMessages.length === 0 && !mentorLoading) {
      callMentor("scenario_start", "Inicio");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessChecked, accessDenied]);

  // ---- main tick loop ----
  useEffect(() => {
    if (!accessChecked || accessDenied || paused || finished) return;
    const delay = highVolatility ? TICK_FAST_MS : TICK_NORMAL_MS;
    const id = setTimeout(() => {
      // advance one candle
      setStep((s) => {
        const next = s + 1;
        if (next >= scenario.totalCandles) {
          setFinished(true);
          return s;
        }
        return next;
      });
    }, delay);
    return () => clearTimeout(id);
  }, [step, accessChecked, accessDenied, paused, finished, highVolatility, scenario.totalCandles]);

  // ---- on step advance: build new candle + update metrics + triggers ----
  useEffect(() => {
    if (step === 0) return;
    const { vol, trend } = scenario.params(step);
    const newCandle = generateCandle(currentCandle, vol, trend);
    setCandles((prev) => [...prev, newCandle]);
    setMetrics({
      ...scenario.base,
      ...scenario.metrics_drift(step, scenario.base),
    });

    // event banner
    const evt = scenario.events[step];
    if (evt) {
      setEventBanner({ ...evt, atStep: step });
      setTimeout(() => setEventBanner(null), 5500);
    }

    // mentor triggers
    const sinceLast = step - lastMentorCandleRef.current;
    if (vol > 0.04 && sinceLast > 8 && !triggeredRef.current.has(`vol-${step}`)) {
      triggeredRef.current.add(`vol-${step}`);
      callMentor("high_volatility", "Volatilidad");
    }

    // idle observation
    if (!position) {
      setIdleCandles((c) => {
        const nc = c + 1;
        if (nc >= 10 && sinceLast > 6) {
          callMentor("idle_10_candles", "Observación");
          return 0;
        }
        return nc;
      });
    } else {
      setIdleCandles(0);
    }

    // ---- SL / TP check on active position ----
    if (position) {
      const hitTp = position.side === "long" ? newCandle.high >= position.tp : newCandle.low <= position.tp;
      const hitSl = position.side === "long" ? newCandle.low <= position.sl : newCandle.high >= position.sl;
      if (hitTp) closePosition(position.tp, "tp");
      else if (hitSl) closePosition(position.sl, "sl");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ---- finished: navigate to results ----
  useEffect(() => {
    if (!finished) return;
    // Close open position at market
    if (position) closePosition(currentPrice, "manual");
    setTimeout(() => {
      navigate("/simulator/results", {
        state: {
          scenarioId,
          scenarioName: scenario.name[lang] || scenario.name.es,
          finalCapital: capital,
          initialCapital: scenario.initialCapital,
          trades,
          decisions,
          isPro,
        },
      });
    }, 1200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  // ---- position helpers ----
  const recordDecision = (txt) => {
    setDecisions((d) => {
      const next = [...d, txt];
      lastDecisionsRef.current = next;
      return next;
    });
  };

  const openPosition = ({ side, sizePct, slPct, tpPct }) => {
    if (position) return;
    const sizeUSD = (capital * sizePct) / 100;
    const sl = side === "long" ? currentPrice * (1 - slPct / 100) : currentPrice * (1 + slPct / 100);
    const tp = side === "long" ? currentPrice * (1 + tpPct / 100) : currentPrice * (1 - tpPct / 100);
    const p = { side, entry: currentPrice, sizePct, sizeUSD, sl, tp, openedAtStep: step };
    setPosition(p);
    recordDecision(`open_${side}_at_${currentPrice.toFixed(2)}_size_${sizePct}%`);
    // trigger feedback if counter-trend
    const t = scenario.params(step).trend;
    if ((side === "long" && t < -0.3) || (side === "short" && t > 0.3)) {
      callMentor("long_in_downtrend", "Estructura");
    }
  };

  const closePosition = (exitPrice, reason) => {
    if (!position) return;
    const direction = position.side === "long" ? 1 : -1;
    const pnlPct = ((exitPrice - position.entry) / position.entry) * 100 * direction;
    const pnlUSD = (position.sizeUSD * pnlPct) / 100;
    setCapital((c) => c + pnlUSD);
    setTrades((tr) => [
      ...tr,
      { ...position, exitPrice, pnlPct, pnlUSD, closedAtStep: step, reason },
    ]);
    recordDecision(`close_${position.side}_pnl_${pnlPct.toFixed(2)}%_reason_${reason}`);
    setPosition(null);
    callMentor("post_trade", "Cierre");
  };

  const moveSLBE = () => {
    if (!position) return;
    setPosition((p) => ({ ...p, sl: p.entry }));
    recordDecision("move_sl_be");
  };

  const panicSell = () => {
    if (!position) return;
    closePosition(currentPrice, "panic");
    recordDecision("panic_sell");
    callMentor("panic_sell", "Psicología");
  };

  const revengeTrade = () => {
    // open opposite of last side at 5% impulsively
    const lastSide = position?.side || (decisions[decisions.length - 1] || "").includes("long") ? "short" : "long";
    if (!position) {
      openPosition({ side: lastSide, sizePct: 5, slPct: 3, tpPct: 6 });
    }
    recordDecision("revenge_trade");
    callMentor("revenge_trade", "Psicología");
  };

  // ---- derived ----
  const livePnlPct = position
    ? ((currentPrice - position.entry) / position.entry) * 100 * (position.side === "long" ? 1 : -1)
    : 0;
  const livePnlUSD = position ? (position.sizeUSD * livePnlPct) / 100 : 0;

  // ---- access gates ----
  const startCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const API = API_BASE; // runtime-resolved
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: "pro_lifetime", origin_url: window.location.origin }),
      });
      const data = await safeJson(res);
      if (res.ok && data?.url) {
        window.location.href = data.url;
        return;
      }
      setCheckoutError("payment");
    } catch {
      setCheckoutError("network");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!accessChecked) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#05060d] text-white/60">
        <p className="font-mono text-xs uppercase tracking-[0.3em]">Cargando simulador…</p>
      </div>
    );
  }

  if (accessDenied === "login") {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-[#05060d] text-center text-white">
        <p className="text-sm text-white/70">{tSim.login_required}</p>
        <button
          onClick={() => navigate("/simulator")}
          className="mt-4 rounded-full bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-black"
        >
          {tSim.cta_free}
        </button>
      </div>
    );
  }

  if (accessDenied === "paywall" || accessDenied === "error") {
    const isPaywall = accessDenied === "paywall";
    return (
      <div className="relative min-h-screen overflow-y-auto bg-[#05060d] text-white" data-testid="sim-play-paywall">
        {/* Subtle ambient grid */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,140,0,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,140,0,0.18) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse at 50% 30%, black 0%, transparent 70%)",
          }}
        />
        <div className="relative mx-auto flex min-h-screen w-full max-w-2xl flex-col items-center justify-center px-5 py-12 text-center">
          <button
            onClick={() => navigate("/simulator")}
            className="absolute left-4 top-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-white md:left-6 md:top-6"
            data-testid="sim-paywall-back"
          >
            <ArrowLeft className="h-3 w-3" /> Volver
          </button>

          {/* Header pill */}
          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.3em] text-amber-200">
            {isPaywall ? (
              <><Lock className="h-3 w-3" /> {tSim.free_used}</>
            ) : (
              <><AlertTriangle className="h-3 w-3" /> Conexión interrumpida</>
            )}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-5 font-display text-3xl font-black tracking-tighter sm:text-4xl md:text-5xl"
          >
            {isPaywall ? tSim.paywall_title : "No pudimos cargar la simulación"}
          </motion.h1>

          <p className="mt-3 max-w-md text-sm text-white/55">
            {isPaywall
              ? "Activa RE₿EL PRO una vez y queda desbloqueado para siempre. Pago único, sin suscripciones."
              : "Comprueba tu conexión. Si el problema persiste, activa RE₿EL PRO o vuelve al menú."}
          </p>

          {/* Pricing card */}
          <div className="mt-8 w-full max-w-md overflow-hidden rounded-3xl border border-orange-400/35 bg-gradient-to-br from-orange-500/[0.08] via-zinc-950 to-zinc-950 p-6 backdrop-blur-xl shadow-[0_0_60px_rgba(255,140,0,0.18)] md:p-8">
            <div className="flex items-baseline justify-center gap-2">
              <span className="font-display text-5xl font-black tracking-tighter text-white">
                €39.90
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">
                / lifetime
              </span>
            </div>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">
              {tSim.cta_note_final}
            </p>

            <ul className="mt-6 space-y-2.5 text-left">
              {tSim.paywall_features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 ring-1 ring-emerald-400/40">
                    <Check className="h-3 w-3 text-emerald-300" />
                  </span>
                  <span className="text-sm text-white/75">{f}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={startCheckout}
              disabled={checkoutLoading}
              data-testid="sim-paywall-checkout"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3.5 text-sm font-black uppercase tracking-[0.14em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.4)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_44px_rgba(255,140,0,0.6)] disabled:cursor-wait disabled:opacity-60"
            >
              <Crown className="h-4 w-4" />
              {checkoutLoading ? "Conectando…" : tSim.cta_btn_final}
            </button>

            {checkoutError && (
              <p className="mt-3 text-[11px] text-rose-300" data-testid="sim-paywall-checkout-error">
                No pudimos abrir el checkout. Inténtalo de nuevo en unos segundos.
              </p>
            )}

            <p className="mt-4 inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              <ShieldCheck className="h-3 w-3" /> Pago seguro · Stripe · IVA incluido
            </p>
          </div>

          <button
            onClick={() => navigate("/simulator")}
            className="mt-6 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-white"
            data-testid="sim-paywall-menu"
          >
            ← {tSim.paywall_close || "Volver al menú"}
          </button>
        </div>
      </div>
    );
  }

  // ---- liquidity zones (display only) ----
  const liquidityZones = [
    { price: scenario.startPrice * 1.02, side: "sell" },
    { price: scenario.startPrice * 0.92, side: "buy" },
  ];

  const totalPnl = ((capital - scenario.initialCapital) / scenario.initialCapital) * 100;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-[#05060d] text-white" data-testid="sim-play-page">
      <style>{`
        @keyframes blink-cursor { 0%,100% { opacity: 1 } 50% { opacity: 0 } }
        @keyframes pulse-dot { 0%,100% { transform: scale(1); opacity: 0.5 } 50% { transform: scale(1.4); opacity: 1 } }
        @keyframes pulse-live { 0%,100% { box-shadow: 0 0 0 0 rgba(0,230,118,0.4) } 50% { box-shadow: 0 0 0 6px rgba(0,230,118,0) } }
        @keyframes pnl-flash-green { 0%,100% { filter: drop-shadow(0 0 0 transparent) } 50% { filter: drop-shadow(0 0 12px rgba(0,230,118,0.6)) } }
        @keyframes pnl-flash-red { 0%,100% { filter: drop-shadow(0 0 0 transparent) } 50% { filter: drop-shadow(0 0 12px rgba(255,51,85,0.6)) } }
      `}</style>

      {/* ===== HEADER BAR ===== */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 bg-black/40 px-3 py-2 backdrop-blur-md md:gap-3 md:px-6 md:py-2.5">
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <button
            onClick={() => navigate("/simulator")}
            data-testid="sim-play-back"
            className="inline-flex shrink-0 items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 hover:text-white md:tracking-[0.22em]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden xs:inline sm:inline">Salir</span>
          </button>
          <span className="hidden md:inline-block h-4 w-px bg-white/15" />
          <div className="min-w-0">
            <p className="hidden md:block text-[9px] font-bold uppercase tracking-[0.3em] text-cyan-300/80">
              ◈ Live Simulation
            </p>
            <p className="truncate font-display text-xs font-bold tracking-tight text-white md:text-base" title={scenario.name[lang] || scenario.name.es}>
              {scenario.name[lang] || scenario.name.es}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 md:gap-4 text-[10px] font-mono uppercase">
          <div className="flex items-center gap-1 text-white/65 md:gap-1.5">
            <Clock className="hidden xs:block h-3 w-3 md:h-3.5 md:w-3.5" />
            <span className="whitespace-nowrap">
              {step + 1}/{scenario.totalCandles}
            </span>
          </div>
          <div className="rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-1 md:px-2.5">
            <span className="hidden sm:inline text-white/45">Cap </span>
            <span className="text-white/90 whitespace-nowrap">${capital.toFixed(0)}</span>
          </div>
          <div
            className={`rounded-md border px-1.5 py-1 md:px-2.5 whitespace-nowrap ${
              totalPnl >= 0
                ? "border-emerald-400/30 text-emerald-300"
                : "border-rose-400/30 text-rose-300"
            }`}
            data-testid="sim-header-pnl"
          >
            {totalPnl >= 0 ? "+" : ""}
            {totalPnl.toFixed(2)}%
          </div>
          {isPro && (
            <span className="hidden md:inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[9px] font-bold tracking-widest text-amber-200">
              <Crown className="h-3 w-3" /> PRO
            </span>
          )}
        </div>
      </div>

      {/* ===== EVENT BANNER ===== */}
      <AnimatePresence>
        {eventBanner && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`flex items-center gap-3 border-b px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] md:px-6 ${
              eventBanner.type === "alert_red"
                ? "border-rose-400/40 bg-rose-500/15 text-rose-200"
                : eventBanner.type === "alert_amber"
                ? "border-amber-400/40 bg-amber-500/15 text-amber-200"
                : "border-cyan-400/40 bg-cyan-500/15 text-cyan-200"
            }`}
            data-testid="sim-event-banner"
          >
            <Zap className="h-3.5 w-3.5" />
            <span>{eventBanner.text[lang] || eventBanner.text.es}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== MAIN GRID =====
          Mobile: chart (compact) → order/position panel → mentor.
          Desktop: chart left (8-9 cols), side column right (Mentor → Order → Position). */}
      <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto p-3 md:grid-cols-12 md:overflow-hidden">
        {/* Chart — reasonable height on mobile so the order panel stays in view */}
        <div className="flex h-[240px] flex-col md:col-span-8 md:h-auto md:min-h-[300px] lg:col-span-9">
          <CandleChart candles={candles} liquidityZones={liquidityZones} />
        </div>

        {/* Right column — on mobile, OrderPanel comes FIRST (most actionable),
            Mentor goes last (informational). Desktop keeps the original order. */}
        <div className="flex min-h-0 flex-col gap-3 md:col-span-4 md:overflow-y-auto lg:col-span-3">
          <div className="order-1 md:order-1 md:hidden">
            <OrderPanel
              disabled={!!position || finished}
              currentPrice={currentPrice}
              onExecute={openPosition}
              onWait={() => recordDecision("wait")}
            />
          </div>
          <div className="order-2 md:order-1 md:hidden">
            <PositionPanel
              position={position}
              currentPrice={currentPrice}
              pnlPct={livePnlPct}
              pnlUSD={livePnlUSD}
              highVolatility={highVolatility}
              onClose={() => closePosition(currentPrice, "manual")}
              onMoveSLBE={moveSLBE}
              onPanicSell={panicSell}
              onRevengeTrade={revengeTrade}
            />
          </div>

          {/* AI Mentor — full size on desktop, collapsible on mobile */}
          <details className="order-3 md:hidden" data-testid="sim-mentor-collapsible">
            <summary className="cursor-pointer rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200 backdrop-blur-sm">
              ◈ AI Mentor {mentorMessages.length > 0 && <span className="ml-1 text-white/40">({mentorMessages.length})</span>}
            </summary>
            <div className="mt-2 min-h-[180px]">
              <AIMentorPanel messages={mentorMessages} isLoading={mentorLoading} observing={!paused} />
            </div>
          </details>

          {/* Desktop layout: Mentor on top, then Order, then Position */}
          <div className="hidden md:flex md:min-h-[180px] md:flex-1">
            <AIMentorPanel messages={mentorMessages} isLoading={mentorLoading} observing={!paused} />
          </div>
          <div className="hidden md:block">
            <OrderPanel
              disabled={!!position || finished}
              currentPrice={currentPrice}
              onExecute={openPosition}
              onWait={() => recordDecision("wait")}
            />
          </div>
          <div className="hidden md:block">
            <PositionPanel
              position={position}
              currentPrice={currentPrice}
              pnlPct={livePnlPct}
              pnlUSD={livePnlUSD}
              highVolatility={highVolatility}
              onClose={() => closePosition(currentPrice, "manual")}
              onMoveSLBE={moveSLBE}
              onPanicSell={panicSell}
              onRevengeTrade={revengeTrade}
            />
          </div>
        </div>
      </div>

      {/* ===== METRICS FOOTER ===== */}
      <div className="shrink-0 border-t border-white/10 p-2 md:p-3">
        <MetricsFooter metrics={metrics} />
      </div>

      {/* finished overlay */}
      {finished && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md">
          <div className="rounded-3xl border border-cyan-400/30 bg-[#080a14] p-10 text-center shadow-[0_0_60px_rgba(0,212,255,0.25)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300/80">Escenario completado</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white">Generando Trader DNA…</h2>
            <div className="mt-5 flex justify-center gap-2">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="inline-block h-2 w-2 rounded-full bg-cyan-400"
                  style={{ animation: "pulse-dot 1.2s infinite", animationDelay: `${i * 0.18}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
